import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { ChevronLeft, ChevronRight } from "lucide-react";
import EventCard from "./EventCard";
import { formatDate } from "@/lib/utils_gg";

const MAX_DESKTOP = 2;

export default function EventCalendarView({ events }) {
  const { t, lang } = useApp();
  const [cursor, setCursor] = useState(() => new Date());
  const y = cursor.getFullYear();
  const m = cursor.getMonth();

  const byDay = useMemo(() => {
    const map = {};
    events.forEach(e => {
      const start = new Date(e.start_date + "T00:00:00");
      const end = new Date((e.end_date || e.start_date) + "T00:00:00");
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        if (d.getFullYear() === y && d.getMonth() === m) {
          const k = d.getDate();
          (map[k] = map[k] || []).push(e);
        }
      }
    });
    return map;
  }, [events, y, m]);

  const firstDay = new Date(y, m, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // week starts Monday
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const [selectedDay, setSelectedDay] = useState(null);
  const [expanded, setExpanded] = useState({});
  const statusOf = e => ["cancelled", "postponed"].includes(e.public_status) ? e.public_status : null;

  return (
    <div data-testid="calendar-view">
      <div className="flex items-center justify-between mb-4 border-b border-foreground pb-3">
        <button data-testid="cal-prev" onClick={() => { setCursor(new Date(y, m - 1, 1)); setSelectedDay(null); setExpanded({}); }} className="gg-btn-ghost"><ChevronLeft className="w-4 h-4" /></button>
        <h2 className="font-display uppercase text-2xl md:text-3xl tracking-tight">{t("monthHeaders")[m]} {y}</h2>
        <button data-testid="cal-next" onClick={() => { setCursor(new Date(y, m + 1, 1)); setSelectedDay(null); setExpanded({}); }} className="gg-btn-ghost"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-7 border border-foreground">
        {["Mo","Tu","We","Th","Fr","Sa","Su"].map(d => (
          <div key={d} className="border-b border-r border-foreground last:border-r-0 eyebrow p-2 text-center bg-muted/40">{d}</div>
        ))}
        {Array.from({ length: startOffset }).map((_, i) => (
          <div key={`x${i}`} className="border-r border-b border-foreground last:border-r-0 min-h-[76px] md:min-h-[112px] bg-muted/20" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const evts = byDay[day] || [];
          const has = evts.length > 0;
          const shown = expanded[day] ? evts : evts.slice(0, MAX_DESKTOP);
          const rest = evts.length - shown.length;
          return (
            <div
              key={day}
              data-testid={`cal-day-${day}`}
              role="button" tabIndex={0}
              onClick={() => setSelectedDay(day)}
              onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedDay(day); } }}
              className={`text-left p-2 min-h-[76px] md:min-h-[112px] border-r border-b border-foreground last:border-r-0 transition-colors duration-200 cursor-pointer ${has ? "hover:bg-muted/60" : ""} ${selectedDay === day ? "bg-secondary text-secondary-foreground" : ""}`}
            >
              <div className="font-display text-lg leading-none">{day}</div>
              {has && (
                <div className="mt-1.5 flex items-center gap-1 md:hidden" data-testid={`cal-dots-${day}`}>
                  {evts.slice(0, 3).map(e => <span key={e.event_id} className={`w-1.5 h-1.5 rounded-full gg-type-dot-${e.event_type} ${statusOf(e) === "cancelled" ? "opacity-40" : ""}`} data-testid={`cal-dot-${e.slug}`} />)}
                  {evts.length > 3 && <span className="text-[10px] leading-none text-muted-foreground ml-0.5">+{evts.length - 3}</span>}
                </div>
              )}
              {has && (
                <div className="hidden md:block mt-1.5 space-y-1" data-testid={`cal-entries-${day}`}>
                  {shown.map(e => {
                    const st = statusOf(e);
                    return (
                      <Link key={e.event_id} to={`/events/${e.slug}`} onClick={ev => ev.stopPropagation()}
                        className={`block min-w-0 group ${st === "cancelled" ? "text-muted-foreground" : "hover:text-primary"} transition-colors`} data-testid={`cal-entry-${e.slug}`}>
                        <span className="flex items-start gap-1.5 min-w-0">
                          <span className={`mt-[5px] w-1.5 h-1.5 rounded-full shrink-0 gg-type-dot-${e.event_type} ${st === "cancelled" ? "opacity-40" : ""}`} />
                          <span className="min-w-0">
                            <span className={`block font-display uppercase text-xs leading-tight tracking-tight truncate ${st === "cancelled" ? "line-through decoration-1" : ""}`}>{e.name}</span>
                            <span className="block text-[10px] text-muted-foreground leading-tight">
                              {st ? <span className={`eyebrow !text-[9px] px-1 py-px gg-status-${st}`}>{t(`public.${st}`)}</span> : (e.start_date === `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}` && e.start_time) || (e.start_date !== e.end_date ? "→" : "")}
                            </span>
                          </span>
                        </span>
                      </Link>
                    );
                  })}
                  {rest > 0 && <button type="button" onClick={ev => { ev.stopPropagation(); setExpanded(x => ({ ...x, [day]: true })); }} className="eyebrow !text-[10px] text-primary hover:underline" data-testid={`cal-more-${day}`}>+{rest} {t("moreEvents")}</button>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {selectedDay && (
        <div className="mt-6" data-testid="cal-day-events">
          <div className="eyebrow mb-3" data-testid="cal-selected-date">{formatDate(`${y}-${String(m+1).padStart(2,"0")}-${String(selectedDay).padStart(2,"0")}`, lang)}</div>
          {(byDay[selectedDay] || []).length === 0 && <div className="text-sm text-muted-foreground">—</div>}
          <div className="grid grid-cols-1 gap-3">
            {(byDay[selectedDay] || []).map(e => <EventCard key={e.event_id} event={e} hideFeatured />)}
          </div>
        </div>
      )}
    </div>
  );
}
